// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Edit, Trash2, Play, CheckCircle, XCircle, Star } from 'lucide-react'
import { formatBaht } from '@/lib/money'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, Skeleton, Modal } from '@/components/ui'
import {
  useWorkOrderDetail,
  useUpdateWorkOrder,
  useDeleteWorkOrder,
} from '@/hooks/useCmms'
import { useAuth } from '@/hooks/useAuth'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import type { WorkOrder } from '@/types'

export function WorkOrderDetailPage() {
  const { t } = useTranslation('cmms')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const workOrderId = Number(id)
  const navigate = useNavigate()
  const { user } = useAuth()
  const formatDate = useDateFormatter()

  const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner'

  const { data: workOrder, isLoading, isFetching } = useWorkOrderDetail(workOrderId)
  const updateWorkOrder = useUpdateWorkOrder()
  const deleteWorkOrder = useDeleteWorkOrder()

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showCompleteModal, setShowCompleteModal] = useState(false)
  const [qualityRating, setQualityRating] = useState(5)

  const handleStartWork = async () => {
    await updateWorkOrder.mutateAsync({ id: workOrderId, status: 'in_progress' })
  }

  const handleCancel = async () => {
    await updateWorkOrder.mutateAsync({ id: workOrderId, status: 'cancelled' })
  }

  const handleComplete = async () => {
    await updateWorkOrder.mutateAsync({
      id: workOrderId,
      status: 'completed',
      quality_rating: qualityRating,
    })
    setShowCompleteModal(false)
  }

  const handleDelete = async () => {
    await deleteWorkOrder.mutateAsync(workOrderId)
    navigate('/work-orders')
  }

  const getStatusBadge = (wo: WorkOrder) => {
    const variants: Record<WorkOrder['status'], 'warning' | 'info' | 'success' | 'ghost'> = {
      open: 'warning',
      in_progress: 'info',
      completed: 'success',
      cancelled: 'ghost',
    }
    return <Badge variant={variants[wo.status]}>{t(`work_order_status.${wo.status}`)}</Badge>
  }

  const getPriorityBadge = (wo: WorkOrder) => {
    const variants: Record<WorkOrder['priority'], 'ghost' | 'warning' | 'error'> = {
      low: 'ghost',
      medium: 'warning',
      high: 'error',
      urgent: 'error',
    }
    return <Badge variant={variants[wo.priority]}>{t(`priorities.${wo.priority}`)}</Badge>
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Card><Skeleton className="h-48" /></Card>
      </div>
    )
  }

  if (!workOrder) {
    return (
      <Card>
        <EmptyState
          title={t('empty.not_found')}
          action={<Button onClick={() => navigate('/work-orders')}>{t('actions.back_to_list')}</Button>}
        />
      </Card>
    )
  }

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs items={[
          { label: t('work_orders.title'), href: '/work-orders' },
          { label: workOrder.title },
        ]} />

        <PageHeader
          title={workOrder.title}
          description={t('messages.created_on', { date: formatDate(workOrder.created_at) })}
          isRefreshing={isFetching && !isLoading}
          // EP-106 Item 1 — primary is the lifecycle step (Start while open,
          // Complete while in progress); Cancel stays available in the
          // overflow for either of those states.
          primaryAction={
            workOrder.status === 'open'
              ? {
                  key: 'start-work',
                  label: t('actions.start_work'),
                  icon: Play,
                  onClick: handleStartWork,
                  loading: updateWorkOrder.isPending,
                }
              : workOrder.status === 'in_progress'
                ? {
                    key: 'complete',
                    label: t('actions.complete'),
                    icon: CheckCircle,
                    onClick: () => setShowCompleteModal(true),
                  }
                : undefined
          }
          overflowActions={[
            {
              key: 'cancel-order',
              label: t('actions.cancel_order'),
              icon: XCircle,
              onClick: handleCancel,
              loading: updateWorkOrder.isPending,
              hidden: !(workOrder.status === 'open' || workOrder.status === 'in_progress'),
            },
            {
              key: 'edit',
              label: tc('actions.edit'),
              icon: Edit,
              to: `/work-orders/${id}/edit`,
            },
            {
              key: 'delete',
              label: tc('actions.delete'),
              icon: Trash2,
              onClick: () => setShowDeleteModal(true),
              destructive: true,
              hidden: !isAdminOrOwner,
            },
          ]}
        />

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Info */}
          <Card className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">{t('work_orders.detail')}</h2>
              {getStatusBadge(workOrder)}
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60">{t('labels.priority')}</dt>
                <dd className="mt-1">{getPriorityBadge(workOrder)}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60">{t('labels.equipment')}</dt>
                <dd className="font-medium break-words">
                  {workOrder.equipment ? (
                    <button
                      className="text-primary hover:underline"
                      onClick={() => navigate(`/equipment/${workOrder.equipment_id}`)}
                    >
                      {workOrder.equipment.name}
                    </button>
                  ) : '-'}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60">{t('labels.assigned_to')}</dt>
                <dd className="font-medium break-words">
                  {workOrder.assigned_to?.name || workOrder.vendor?.name
                    ? `${workOrder.assigned_to?.name || workOrder.vendor?.name}`
                    : '-'}
                </dd>
              </div>
              {workOrder.maintenance_request && (
                <div className="min-w-0">
                  <dt className="text-sm text-base-content/60">{t('labels.maintenance_request')}</dt>
                  <dd className="font-medium break-words">
                    <button
                      className="text-primary hover:underline"
                      onClick={() => navigate(`/maintenances/${workOrder.maintenance_request!.id}`)}
                    >
                      #{workOrder.maintenance_request.id} - {workOrder.maintenance_request.title}
                    </button>
                  </dd>
                </div>
              )}
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60">{t('labels.due_date')}</dt>
                <dd className="font-medium">
                  {workOrder.due_date ? formatDate(workOrder.due_date) : '-'}
                </dd>
              </div>
              {workOrder.completed_at && (
                <div className="min-w-0">
                  <dt className="text-sm text-base-content/60">{t('labels.completed_at')}</dt>
                  <dd className="font-medium">{formatDate(workOrder.completed_at)}</dd>
                </div>
              )}
              {workOrder.completed_by && (
                <div className="min-w-0">
                  <dt className="text-sm text-base-content/60">{t('labels.completed_by')}</dt>
                  <dd className="font-medium break-words">{workOrder.completed_by.name}</dd>
                </div>
              )}
              {workOrder.quality_rating && (
                <div className="min-w-0">
                  <dt className="text-sm text-base-content/60">{t('labels.quality_rating')}</dt>
                  <dd className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${i < workOrder.quality_rating! ? 'text-warning fill-warning' : 'text-base-300'}`}
                      />
                    ))}
                  </dd>
                </div>
              )}
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60">{t('labels.estimated_cost')}</dt>
                <dd className="font-medium">
                  {workOrder.estimated_cost != null ? formatBaht(Number(workOrder.estimated_cost)) : '-'}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60">{t('labels.actual_cost')}</dt>
                <dd className="font-medium">
                  {workOrder.actual_cost != null ? formatBaht(Number(workOrder.actual_cost)) : '-'}
                </dd>
              </div>
            </dl>

            {workOrder.description && (
              <div className="border-t border-base-200 pt-4 mt-4">
                <h3 className="font-medium mb-2">{t('labels.description')}</h3>
                <p className="text-base-content/70 whitespace-pre-wrap">{workOrder.description}</p>
              </div>
            )}
            {workOrder.notes && (
              <div className="border-t border-base-200 pt-4 mt-4">
                <h3 className="font-medium mb-2">{t('labels.notes')}</h3>
                <p className="text-base-content/70 whitespace-pre-wrap">{workOrder.notes}</p>
              </div>
            )}
          </Card>

          {/* Status Card */}
          <Card className={`${
            workOrder.status === 'completed' ? 'bg-success/5 border-success' :
            workOrder.status === 'in_progress' ? 'bg-info/5 border-info' :
            workOrder.status === 'cancelled' ? 'bg-base-200' :
            'bg-warning/5 border-warning'
          }`}>
            <div className="text-sm text-base-content/60 mb-1">{tc('labels.status')}</div>
            <div className="text-xl font-bold mb-4">{t(`work_order_status.${workOrder.status}`)}</div>
            {workOrder.status === 'open' && (
              <Button className="w-full" onClick={handleStartWork} loading={updateWorkOrder.isPending}>
                {t('actions.start_work')}
              </Button>
            )}
            {workOrder.status === 'in_progress' && (
              <Button className="w-full" onClick={() => setShowCompleteModal(true)}>
                {t('actions.complete')}
              </Button>
            )}
          </Card>
        </div>

        {/* Images */}
        {workOrder.image_urls && workOrder.image_urls.length > 0 && (
          <Card>
            <h2 className="text-lg font-semibold mb-4">{t('labels.images')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {workOrder.image_urls.map((url, index) => (
                <a
                  key={index}
                  href={`${import.meta.env.VITE_API_URL || ''}/api/images/${url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="aspect-square rounded-lg overflow-hidden border border-base-200 hover:border-primary transition-colors"
                >
                  <img
                    src={`${import.meta.env.VITE_API_URL || ''}/api/images/${url}`}
                    alt={`${workOrder.title} ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </a>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Delete Work Order Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={t('messages.delete_work_order_title')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              variant="error"
              onClick={handleDelete}
              loading={deleteWorkOrder.isPending}
            >
              {tc('actions.delete')}
            </Button>
          </>
        }
      >
        <p>{t('messages.delete_work_order_confirm', { title: workOrder.title })}</p>
      </Modal>

      {/* Complete Work Order Modal */}
      <Modal
        isOpen={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        title={t('messages.complete_title')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowCompleteModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              onClick={handleComplete}
              loading={updateWorkOrder.isPending}
            >
              {t('actions.complete')}
            </Button>
          </>
        }
      >
        <p className="mb-4">{t('messages.complete_description')}</p>
        <div className="flex gap-2 justify-center">
          {[1, 2, 3, 4, 5].map((rating) => (
            <button
              key={rating}
              onClick={() => setQualityRating(rating)}
              className="p-1 transition-transform hover:scale-110"
              aria-label={`${rating} stars`}
            >
              <Star
                className={`w-8 h-8 ${
                  rating <= qualityRating ? 'text-warning fill-warning' : 'text-base-300'
                }`}
              />
            </button>
          ))}
        </div>
      </Modal>
    </>
  )
}
